(function () {
  const MOBILE_MQ = '(max-width: 749px)';
  const REDUCE_MQ = '(prefers-reduced-motion: reduce)';
  const MAX_SWIPER_WAIT_FRAMES = 120;

  class TbcUspBarSlider extends HTMLElement {
    connectedCallback() {
      this.readSettings();
      this.mql = this.mql || window.matchMedia(MOBILE_MQ);
      this.motionMql = this.motionMql || window.matchMedia(REDUCE_MQ);
      this._initToken = (this._initToken || 0) + 1;
      const token = this._initToken;

      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);

      this.destroySwiper();
      this.unbindMedia();
      this.bindFocus();
      this.bindVisibility();

      if (!this.mobile.enabled && !this.desktop.enabled) return;

      this.whenSwiperReady(token, () => {
        if (!this.isConnected || token !== this._initToken) return;
        this.onBreakpoint();
        this.bindMedia();
      });
    }

    disconnectedCallback() {
      this._initToken = (this._initToken || 0) + 1;
      this.unbindMedia();
      this.unbindVisibility();
      this.unbindFocus();
      this.destroySwiper();
    }

    readSettings() {
      this._readyAttempts = 0;
      this.mobile = {
        enabled: this.dataset.sliderMobile === 'true',
        autoplay: this.dataset.autoplayMobile === 'true',
        delay: Number(this.dataset.autoplayDelayMobile) || 4000,
        loop: this.dataset.loopMobile === 'true',
        direction: this.dataset.directionMobile === 'vertical' ? 'vertical' : 'horizontal',
      };
      this.desktop = {
        enabled: this.dataset.sliderDesktop === 'true',
        autoplay: this.dataset.autoplayDesktop === 'true',
        delay: Number(this.dataset.autoplayDelayDesktop) || 4000,
        loop: this.dataset.loopDesktop === 'true',
        direction: this.dataset.directionDesktop === 'vertical' ? 'vertical' : 'horizontal',
        continuous: this.dataset.continuousDesktop === 'true',
      };
      this.columnsMobile = this.clampColumns(this.dataset.columnsMobile);
      this.columnsDesktop = this.clampColumns(this.dataset.columnsDesktop);
    }

    clampColumns(value) {
      return Math.min(4, Math.max(1, parseInt(value, 10) || 3));
    }

    activeConfig() {
      return this.mql.matches ? this.mobile : this.desktop;
    }

    bindMedia() {
      if (this._mediaBound) return;
      this._mediaBound = true;
      this.mql.addEventListener('change', this.onBreakpoint);
      this.motionMql.addEventListener('change', this.onMotionChange);
    }

    unbindMedia() {
      if (!this._mediaBound) return;
      this._mediaBound = false;
      this.mql.removeEventListener('change', this.onBreakpoint);
      this.motionMql.removeEventListener('change', this.onMotionChange);
    }

    bindFocus() {
      if (this._focusBound) return;
      this._focusBound = true;
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
    }

    unbindFocus() {
      if (!this._focusBound) return;
      this._focusBound = false;
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
    }

    bindVisibility() {
      if (this._visibilityBound) return;
      this._visibilityBound = true;
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    unbindVisibility() {
      if (!this._visibilityBound) return;
      this._visibilityBound = false;
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    whenSwiperReady(token, callback) {
      if (!this.isConnected || token !== this._initToken) return;
      if (typeof window.Swiper !== 'undefined') {
        callback();
        return;
      }
      if (this._readyAttempts >= MAX_SWIPER_WAIT_FRAMES) return;
      this._readyAttempts += 1;
      requestAnimationFrame(() => this.whenSwiperReady(token, callback));
    }

    onBreakpoint() {
      const config = this.activeConfig();
      if (!config || !config.enabled) {
        this.destroySwiper();
        return;
      }
      this.createSwiper();
    }

    onMotionChange() {
      this.destroySwiper();
      this.onBreakpoint();
    }

    onVisibility() {
      if (document.hidden) {
        this.swiper?.autoplay?.stop();
        return;
      }
      this.resumeAutoplay();
    }

    onFocusIn() {
      this._pausedByFocus = true;
      this.swiper?.autoplay?.stop();
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this._pausedByFocus = false;
      this.resumeAutoplay();
    }

    resumeAutoplay() {
      if (!this.swiper?.autoplay) return;
      if (this._pausedByEditor || this._pausedByFocus || document.hidden) return;
      if (this.motionMql.matches) return;
      const config = this.activeConfig();
      if (!config?.enabled) return;
      const continuous = this.isContinuous(config);
      if (!config.autoplay && !continuous) return;
      this.swiper.autoplay.start();
    }

    isContinuous(config) {
      return !this.mql.matches && config.direction === 'vertical' && config.continuous === true;
    }

    createSwiper() {
      this.destroySwiper();
      if (typeof window.Swiper === 'undefined') return;

      const config = this.activeConfig();
      if (!config?.enabled) return;

      const el = this.querySelector('.tbc-section-usp-bar__swiper');
      if (!el) return;

      const isMobile = this.mql.matches;
      const perViewSetting = isMobile ? this.columnsMobile : this.columnsDesktop;
      const direction = config.direction === 'vertical' ? 'vertical' : 'horizontal';
      const reduceMotion = this.motionMql.matches;
      const continuous = this.isContinuous(config) && !reduceMotion;
      const realSlides = el.querySelectorAll('.swiper-slide:not([data-ticker-clone])');
      const realCount = realSlides.length;
      if (!realCount) return;

      const perView = Math.min(perViewSetting, realCount);
      this._perView = perView;
      this.classList.toggle('is-single-view', perView === 1);
      this.classList.toggle('is-vertical', direction === 'vertical');
      this.classList.toggle('is-ticker', continuous);

      if (realCount <= perView && !continuous) {
        this.classList.add('is-swiper-static');
        if (direction === 'vertical') this.applyVerticalMetrics(el, perView);
        else this.clearVerticalMetrics(el);
        return;
      }

      if (continuous) this.ensureTickerClones(el, perView);

      const totalCount = el.querySelectorAll('.swiper-slide').length;
      const canLoop = continuous
        ? totalCount > perView
        : config.loop && !reduceMotion && realCount > perView;
      const useAutoplay = (config.autoplay || continuous) && !reduceMotion;

      if (direction === 'vertical') this.applyVerticalMetrics(el, perView);
      else this.clearVerticalMetrics(el);

      const speed = reduceMotion ? 0 : continuous ? Math.max(config.delay, 800) : 450;

      this.swiper = new window.Swiper(el, {
        direction,
        slidesPerView: perView,
        slidesPerGroup: 1,
        spaceBetween: 0,
        speed,
        loop: canLoop,
        rewind: !canLoop,
        loopAdditionalSlides: perView,
        autoplay: useAutoplay
          ? {
              delay: continuous ? 1 : config.delay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        grabCursor: !continuous,
        allowTouchMove: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        autoHeight: direction === 'horizontal' && perView === 1,
        roundLengths: true,
        watchSlidesProgress: true,
        a11y: { enabled: !continuous },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        on: {
          init: (swiper) => {
            this.classList.add('is-swiper');
            this.updateSlideAccess();
            if (useAutoplay && swiper.autoplay) swiper.autoplay.start();
          },
          slideChange: () => {
            this.updateSlideAccess();
          },
        },
      });

      this.bindResize(el);
    }

    ensureTickerClones(el, perView) {
      const wrapper = el.querySelector('.swiper-wrapper');
      if (!wrapper) return;
      const originals = [...wrapper.querySelectorAll('.swiper-slide:not([data-ticker-clone])')];
      if (!originals.length) return;
      const target = Math.max(perView * 2, perView + 1);
      let guard = 0;
      while (wrapper.querySelectorAll('.swiper-slide').length < target && guard < 24) {
        const source = originals[guard % originals.length];
        const clone = source.cloneNode(true);
        clone.setAttribute('data-ticker-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        [...clone.attributes].forEach((attr) => {
          if (attr.name.indexOf('shopify') !== -1) clone.removeAttribute(attr.name);
        });
        wrapper.appendChild(clone);
        guard += 1;
      }
    }

    applyVerticalMetrics(el, perView) {
      const slides = [...el.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')];
      if (!slides.length) return;

      slides.forEach((slide) => {
        slide.style.height = 'auto';
      });

      let max = 0;
      slides.forEach((slide) => {
        max = Math.max(max, Math.ceil(slide.scrollHeight));
      });
      slides.forEach((slide) => {
        slide.style.height = '';
      });
      if (max < 1) return;

      const signature = `${max}:${perView}`;
      if (signature === this._metricSignature) return;
      this._metricSignature = signature;

      const section = this.closest('.tbc-section-usp-bar');
      if (section) {
        const prop = this.mql.matches ? '--tbc-usp-bar-row-height-mobile' : '--tbc-usp-bar-row-height';
        section.style.setProperty(prop, `${max}px`);
      }
      el.style.height = `${max * perView}px`;
    }

    clearVerticalMetrics(el) {
      this._metricSignature = '';
      el.style.height = '';
      const section = this.closest('.tbc-section-usp-bar');
      if (!section) return;
      section.style.removeProperty('--tbc-usp-bar-row-height');
      section.style.removeProperty('--tbc-usp-bar-row-height-mobile');
    }

    bindResize(el) {
      if (typeof ResizeObserver === 'undefined') return;
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.resizeObserver = new ResizeObserver(() => {
        if (this._measuring || !this.swiper) return;
        if (this.swiper.params.direction !== 'vertical') return;
        this._measuring = true;
        const previous = this._metricSignature;
        this.applyVerticalMetrics(el, this._perView || 1);
        if (this._metricSignature !== previous) this.swiper.update();
        this._measuring = false;
      });
      this.resizeObserver.observe(this);
    }

    updateSlideAccess() {
      this.querySelectorAll('.swiper-slide').forEach((slide) => {
        const visible =
          slide.classList.contains('swiper-slide-visible') ||
          slide.classList.contains('swiper-slide-active');
        slide.querySelectorAll('a').forEach((link) => {
          if (visible) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        });
      });
    }

    destroySwiper() {
      this.classList.remove('is-swiper', 'is-swiper-static', 'is-single-view', 'is-vertical', 'is-ticker');
      this._metricSignature = '';
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.querySelectorAll('[data-ticker-clone]').forEach((node) => node.remove());
      const el = this.querySelector('.tbc-section-usp-bar__swiper');
      if (!el) return;
      el.style.height = '';
      el.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.style.height = '';
      });
    }

    slideToBlock(blockId) {
      if (!this.swiper || !blockId) return;
      const slide = this.querySelector(
        `.swiper-slide[data-block-id="${blockId}"]:not(.swiper-slide-duplicate):not([data-ticker-clone])`
      );
      if (!slide) return;
      const index = Number(slide.getAttribute('data-swiper-slide-index'));
      const fallbackIndex = Array.from(
        this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate):not([data-ticker-clone])')
      ).indexOf(slide);
      const target = Number.isFinite(index) ? index : fallbackIndex;
      if (target < 0) return;

      if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
        this.swiper.slideToLoop(target);
      } else {
        this.swiper.slideTo(target);
      }
    }
  }

  if (!customElements.get('tbc-usp-bar-slider')) {
    customElements.define('tbc-usp-bar-slider', TbcUspBarSlider);
  }

  if (!window.__tbcUspBarSliderBound) {
    window.__tbcUspBarSliderBound = true;

    document.addEventListener('shopify:section:load', (event) => {
      const slider = event.target.querySelector?.('tbc-usp-bar-slider');
      if (!slider) return;
      slider.disconnectedCallback?.();
      slider.connectedCallback?.();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelector?.('tbc-usp-bar-slider')?.destroySwiper?.();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      const slider = block.closest?.('tbc-usp-bar-slider');
      if (!slider) return;
      slider._pausedByEditor = true;
      const blockId = block.dataset?.blockId || event.detail?.blockId;
      slider.slideToBlock?.(blockId);
      slider.swiper?.autoplay?.stop();
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const slider = event.target.closest?.('tbc-usp-bar-slider');
      if (!slider) return;
      slider._pausedByEditor = false;
      slider.resumeAutoplay?.();
    });
  }
})();
